import { produce } from "immer";
import pluralize from "pluralize";
import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Issue, IssueType } from "@protos/issues/v1/issues";
import {
  createNangoTickets,
  CreateNangoTicketsRequest,
  CreateNangoTicketsResponse,
} from "@shared/api/lib/ticketing/createNangoTicket";
import { useIssuesFilterState } from "@shared/findings";
import { ApiError } from "@shared/types";
import { FindingsFilters } from "@shared/types";
import { timeLookbackRangeToSinceDate } from "@shared/utils";

import { useOrg } from "../useOrg";
import { useTimeLookbackRange } from "../useTimeLookbackRange";

const showNotifications = ({
  succeeded,
  failed,
  skipped,
}: CreateNangoTicketsResponse) => {
  if (failed.length > 0) {
    const retry_message =
      "Semgrep will retry several times over the next 24 hours";
    if (succeeded.length > 0) {
      showNotification({
        title: "Some tickets were not created",
        message: `There was a problem creating ${failed.length} of ${
          failed.length + succeeded.length
        } tickets in your request. ${retry_message}.`,
        color: "yellow",
        autoClose: 10000,
      });
    } else {
      showNotification({
        title: "Ticket not created",
        message:
          failed.length > 1
            ? `There was a problem creating tickets for these findings. ${retry_message}.`
            : `There was a problem creating a ticket for this finding. ${retry_message}.`,
        color: "red",
        autoClose: 10000,
      });
    }
    failed.forEach((failure) => console.warn(failure.error));
  }
  if (succeeded.length === 1) {
    showNotification({
      title: "Ticket created for finding",
      message: `${succeeded[0].external_slug} was created. Click here to view it.`,
      color: "green",
      autoClose: 10000,
      onClick: () => {
        window.open(succeeded[0].ticket_url, "_blank");
      },
      styles: {
        root: {
          cursor: "pointer",
        },
      },
    });
  } else if (succeeded.length > 0) {
    showNotification({
      title: "Tickets created for findings",
      message: `${succeeded.length} tickets were created.`,
      color: "green",
    });
  }
  if (skipped.length) {
    const skipped_issue_ids = skipped.flatMap((result) => result.issue_ids);
    showNotification({
      title: "Issues skipped",
      message:
        skipped_issue_ids.length > 1
          ? `${skipped_issue_ids.length} issues were skipped because they already have tickets.`
          : "1 issue was skipped because it already has a ticket",
      color: "yellow",
    });
  }
};

export const useCreateNangoTicketMutation = (issueType: IssueType) => {
  const [org] = useOrg();
  const queryClient = useQueryClient();
  const [timeLookbackRange] = useTimeLookbackRange();
  const { issuesFilterProto } = useIssuesFilterState(issueType);

  return useMutation({
    mutationFn: async (
      variables: {
        filterOverrides?: FindingsFilters;
      } & CreateNangoTicketsRequest
    ) => {
      // Handle the case where the user selected rule ids and specific findings
      const issueIdsLength = variables.issueIds?.length || 0;
      const hasRuleId = variables.filterOverrides?.rule_id;

      const promises: Promise<CreateNangoTicketsResponse>[] = [];
      if (hasRuleId) {
        promises.push(
          createNangoTickets({
            deploymentId: variables.deploymentId,
            ticketingId: variables.ticketingId,
            issueType,
            issuesFilter: {
              ...issuesFilterProto,
              since: timeLookbackRangeToSinceDate(timeLookbackRange),
              ruleIds:
                variables.filterOverrides?.rule_id
                  ?.split(",")
                  .map((num) => parseInt(num, 10)) ?? [],
            },
            multiIssueTickets: variables.multiIssueTickets,
            projectId: variables.projectId,
          })
        );
      }
      if (issueIdsLength > 0) {
        promises.push(createNangoTickets({ ...variables, issueType }));
      }
      return Promise.all(promises);
    },
    onSuccess: (responses) => {
      responses.forEach(({ succeeded, failed, skipped }) => {
        showNotifications({ succeeded, failed, skipped });
        // Handle case where we create ticket in findings list page
        queryClient.invalidateQueries({
          queryKey: ["DeploymentFindings", org?.id],
        });
        // Handles case where we create ticket in finding detail page
        succeeded.forEach((ticket) => {
          ticket.issue_ids.forEach((issue_id) => {
            queryClient.setQueryData(
              ["finding", issue_id, org?.id],
              (old: Issue) =>
                produce(old as Issue, (draft) => {
                  if (draft) {
                    draft.externalTicket = {
                      url: ticket.ticket_url,
                      externalSlug: ticket.external_slug,
                    };
                  }
                })
            );
          });
        });
      });
    },
    onError: (err: ApiError, variables) => {
      // Backend timeout from AI analysis
      // TODO: this needs to be replaced with a more robust way
      // of catching when the analysis is timing out vs other causes
      if (err.statusCode === 500) {
        showNotification({
          title: `${pluralize(
            "Ticket",
            variables.issueIds?.length
          )} still processing`,
          message: `Check your ticketing system in 1-2 minutes to see results`,
          autoClose: 10000,
        });
      } else {
        showNotification({
          title: `${pluralize(
            "Ticket",
            variables.issueIds?.length
          )} not created`,
          message: `Error creating ${pluralize(
            "ticket",
            variables.issueIds?.length
          )}: ${err}`,
          color: "red",
        });
      }
    },
  });
};
